import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME } from "../ApiProvider";
import { api } from "./api";
import { USE_RETRIEVE_ADDABLE_TEAMS } from "./queryKeys";
const USE_RETRIEVE_LEAGUE = "useRetrieveLeague";
const USE_RETRIEVE_LEAGUES_BY_PLAYER_ID = "useRetrieveLeaguesByPlayerId";
const USE_RETRIEVE_LEAGUE_TEAMS = "useRetrieveLeagueTeams";
const USE_RETRIEVE_ALL_LEAGUE_TEAMS = "useRetrieveAllLeagueTeams";
export const useRetrieveLeague = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_LEAGUE, id],
        queryFn: async () => {
            const { data } = await api.get(`/leagues/${id}/`);
            return data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveLeaguesByPlayerId = ({ playerId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_LEAGUES_BY_PLAYER_ID, { playerId }],
        queryFn: async () => {
            const { data } = await api.get(`/players/${playerId}/leagues/`);
            return data;
        },
        enabled: !!playerId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveLeagueTeams = ({ leagueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_LEAGUE_TEAMS, leagueId],
        queryFn: async () => {
            const { data } = await api.get(`/leagues/${leagueId}/teams/`);
            return data;
        },
        enabled: !!leagueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveAllLeagueTeams = ({ leagueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_ALL_LEAGUE_TEAMS, leagueId],
        queryFn: async () => {
            const { data } = await api.get(`/leagues/${leagueId}/teams/`);
            return data;
        },
        enabled: !!leagueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveAddableTeams = ({ leagueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_ADDABLE_TEAMS, leagueId],
        queryFn: async () => {
            const { data } = await api.get(`/leagues/${leagueId}/teams/addable/`);
            return data;
        },
        enabled: !!leagueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreateLeague = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post(`/leagues/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUES_BY_PLAYER_ID]);
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateLeague = ({ id, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.put(`/leagues/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUES_BY_PLAYER_ID]);
            onSuccess(data);
        },
        onError,
    });
};
export const useDeleteLeague = ({ id, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.delete(`/leagues/${id}/`);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.removeQueries([USE_RETRIEVE_LEAGUE, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUES_BY_PLAYER_ID]);
            onSuccess(data, undefined);
        },
        onError,
    });
};
export const useCreateTeamAndAddToLeague = ({ leagueId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (teamData) => {
            return api.post(`/leagues/${leagueId}/teams/create/`, teamData);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE, leagueId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useAddExistingTeamToLeague = ({ leagueId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ teamIds }) => {
            return api.post(`/leagues/${leagueId}/teams/add-existing/`, { teamIds });
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_ADDABLE_TEAMS, leagueId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateTeamMembershipStatus = ({ leagueId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.put(`/leagues/${leagueId}/teams/update-status/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_ALL_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE, leagueId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useRemoveTeamFromLeague = ({ leagueId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ teamId }) => {
            return api.delete(`/leagues/${leagueId}/teams/remove/`, {
                data: { teamId },
            });
        },
        onMutate,
        onSuccess: () => {
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_ALL_LEAGUE_TEAMS, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE, leagueId]);
            queryClient.invalidateQueries([USE_RETRIEVE_ADDABLE_TEAMS, leagueId]);
            onSuccess();
        },
        onError,
    });
};
